import { ChevronRight, Ellipsis, Pencil, Trash } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router'

import { ConfirmDialog } from '@/components/ConfirmDialog'
import { StatusBadge } from '@/components/ds'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Skeleton } from '@/components/ui/skeleton'
import { TableCell, TableRow } from '@/components/ui/table'
import { Warehouse } from '@/interfaces/warehouse/Warehouse'
import { cn } from '@/lib/utils'
import { formatQuantity } from '@/lib/warehouse/stockLevels'

export interface WarehouseStats {
  items: number
  onHand: number
  reserved: number
  low: number
  out: number
}

interface WarehouseItemProps {
  warehouse?: Warehouse
  stats?: WarehouseStats
  onEdit?: (warehouse: Warehouse) => void
  onDelete?: (warehouse: Warehouse) => Promise<void>
}

export const WarehouseItem = ({ warehouse, stats, onEdit, onDelete }: WarehouseItemProps) => {
  const navigate = useNavigate()
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)

  if (!warehouse) {
    return (
      <TableRow className="hover:bg-transparent">
        {Array.from({ length: 7 }).map((_, i) => (
          <TableCell key={i} className={cn(![0, 3, 6].includes(i) && 'hidden md:table-cell')}>
            <Skeleton className="h-4 w-full max-w-24" />
          </TableCell>
        ))}
      </TableRow>
    )
  }

  const attention = (stats?.low ?? 0) + (stats?.out ?? 0)

  return (
    <>
      <TableRow className="group cursor-pointer" onClick={() => navigate(`/warehouse/warehouse/${warehouse.id}`)}>
        <TableCell className="max-w-0 w-full">
          <div className="flex items-center gap-2 md:min-w-40">
            <span className="truncate font-medium">{warehouse.name}</span>
            {warehouse.isDefault && <StatusBadge status="issued" label="Výchozí" className="hidden md:inline-flex" />}
          </div>
          <p className="truncate text-xs text-muted-foreground md:hidden">
            {[
              warehouse.isDefault ? 'Výchozí' : null,
              warehouse.code,
              stats ? `${formatQuantity(stats.items)} položek` : null,
              warehouse.isActive === false ? 'Neaktivní' : null,
            ]
              .filter(Boolean)
              .join(', ')}
            {stats && attention > 0 && (
              <span className={cn(stats.out ? 'text-danger' : 'text-warning')}>, dochází {attention}</span>
            )}
          </p>
        </TableCell>
        <TableCell className="hidden text-muted-foreground md:table-cell">{warehouse.code}</TableCell>
        <TableCell className="hidden text-right md:table-cell">{stats ? formatQuantity(stats.items) : '-'}</TableCell>
        <TableCell className="text-right">{stats ? `${formatQuantity(stats.onHand)} ks` : '-'}</TableCell>
        <TableCell className="hidden text-right md:table-cell">
          {stats ? (
            <span className={cn(stats.out ? 'text-danger' : attention ? 'text-warning' : 'text-muted-foreground')}>
              {attention ? `${attention} položek` : 'Vše v pořádku'}
            </span>
          ) : (
            '-'
          )}
        </TableCell>
        <TableCell className="hidden md:table-cell">
          {warehouse.isActive !== false ? (
            <StatusBadge status="paid" label="Aktivní" />
          ) : (
            <StatusBadge status="draft" label="Neaktivní" />
          )}
        </TableCell>
        <TableCell className="w-px pl-0 text-right" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-end gap-1">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon-sm" aria-label="Akce">
                  <Ellipsis />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onEdit?.(warehouse)}>
                  <Pencil />
                  Upravit
                </DropdownMenuItem>
                <DropdownMenuItem variant="destructive" onClick={() => setIsDeleteDialogOpen(true)}>
                  <Trash />
                  Smazat
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <ChevronRight className="hidden size-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 md:block" />
          </div>
        </TableCell>
      </TableRow>

      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        setIsOpen={setIsDeleteDialogOpen}
        title={`Smazat sklad ${warehouse.name}?`}
        description="Tato akce je nevratná. Opravdu chcete smazat tento sklad?"
        onConfirm={async () => {
          await onDelete?.(warehouse)
          setIsDeleteDialogOpen(false)
        }}
      />
    </>
  )
}
